import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { TrackId } from './types';
import { setOverlayTrack } from '../auth/overlay';

const STORAGE_KEY = 'proto-track';
const DEFAULT_TRACK: TrackId = 'B';

function readStored(): TrackId {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'A' || raw === 'B' ? raw : DEFAULT_TRACK;
  } catch {
    return DEFAULT_TRACK;
  }
}

interface TrackContextValue {
  track: TrackId;
  setTrack: (next: TrackId) => void;
}

const TrackContext = createContext<TrackContextValue>({
  track: DEFAULT_TRACK,
  setTrack: () => undefined,
});

export function TrackProvider({ children }: { children: ReactNode }) {
  const [track, setTrackState] = useState<TrackId>(readStored);

  const setTrack = useCallback((next: TrackId) => {
    setOverlayTrack(next);
    setTrackState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Приватный режим браузера — переключатель просто не переживёт перезагрузку.
    }
  }, []);

  const value = useMemo(() => ({ track, setTrack }), [track, setTrack]);
  return <TrackContext.Provider value={value}>{children}</TrackContext.Provider>;
}

export function useTrack(): TrackContextValue {
  return useContext(TrackContext);
}
